import { WelcomeBoardItem } from '../../../common/interfaces';
import { GridPosition } from './types';

// Grid parsing utilities
export const parseGridTemplate = (template: string): number => {
    const match = template.match(/repeat\((\d+),/);
    return match ? parseInt(match[1]) : 3;
};

// Parse grid position (handles both single cell and span syntax)
export const parseGridPosition = (position?: string): GridPosition => {
    if (!position) return { start: 1, span: 1 };
    
    // Handle "1 / span 3" format
    const spanMatch = position.match(/(\d+)\s*\/\s*span\s+(\d+)/);
    if (spanMatch) {
        return {
            start: parseInt(spanMatch[1]),
            span: parseInt(spanMatch[2])
        };
    }
    
    // Handle "1 / 4" format (start / end)
    const rangeMatch = position.match(/(\d+)\s*\/\s*(\d+)/);
    if (rangeMatch) {
        const start = parseInt(rangeMatch[1]);
        const end = parseInt(rangeMatch[2]);
        return {
            start: start,
            span: end - start
        };
    }
    
    // Handle simple number format "1"
    const simpleMatch = position.match(/(\d+)/);
    if (simpleMatch) {
        return {
            start: parseInt(simpleMatch[1]),
            span: 1
        };
    }
    
    return { start: 1, span: 1 };
};

// Check if a grid area is occupied by any widget
export const isGridAreaOccupied = (
    targetCol: number, 
    targetRow: number, 
    widgets: WelcomeBoardItem[]
): boolean => {
    return widgets.some(widget => {
        const colPos = parseGridPosition(widget.gridColumn);
        const rowPos = parseGridPosition(widget.gridRow);
        
        const colStart = colPos.start;
        const colEnd = colStart + colPos.span;
        const rowStart = rowPos.start;
        const rowEnd = rowStart + rowPos.span;
        
        return targetCol >= colStart && targetCol < colEnd && 
               targetRow >= rowStart && targetRow < rowEnd;
    });
};

// Convert user-friendly position values to CSS grid string
export const formatGridPosition = (start: number, span: number): string => {
    if (span === 1) {
        return start.toString();
    }
    return `${start} / span ${span}`;
};

// Check if a widget with given position/size would overlap with existing widgets
export const wouldOverlap = (
    colStart: number,
    colSpan: number,
    rowStart: number,
    rowSpan: number,
    widgets: WelcomeBoardItem[]
): boolean => {
    for (let col = colStart; col < colStart + colSpan; col++) {
        for (let row = rowStart; row < rowStart + rowSpan; row++) {
            if (isGridAreaOccupied(col, row, widgets)) {
                return true;
            }
        }
    }
    return false;
};

// Get maximum span for a given start position
export const getMaxSpan = (start: number, maxGrid: number): number => {
    return maxGrid - start + 1;
};